import {
  buildExternalJobsFilterParams,
  getExternalJobsKey,
  loadExternalJobDays,
} from 'redux/modules/externalJobs';
import {
  dayKey,
  getExternalFloor,
  getMissingExternalRange,
  splitFeatured,
} from './jobsTimeline';

/**
 * The day range of external jobs the timeline still needs, or null.
 *
 * @param {object} args
 * @param {Array} args.briefs Loaded Twine jobs in API order.
 * @param {boolean} args.moreToLoad
 * @param {object} args.filters Board filters (see buildExternalJobsFilterParams).
 * @param {object|undefined} args.entry externalJobs state for this key.
 * @param {number} args.externalDaysBack
 * @param {string} args.timeZone
 * @param {number} [args.now]
 * @returns {{from: string, to: string}|null}
 */
export const getExternalJobsRequest = ({
  briefs,
  moreToLoad,
  filters,
  entry,
  externalDaysBack = 0,
  timeZone,
  now = Date.now(),
}) => {
  // Easy apply only hides external jobs, so there's nothing to load.
  if (filters.internal) {
    return null;
  }
  const today = dayKey(now, timeZone);
  const floor = getExternalFloor({
    briefs: splitFeatured(briefs).rest,
    moreToLoad,
    externalDaysBack,
    today,
    timeZone,
  });
  return getMissingExternalRange(floor, today, entry ? entry.fetched : {});
};

/**
 * Load the external jobs for the days the loaded Twine jobs cover. Used by
 * the server render, which reads plain Redux state.
 *
 * @param {Function} dispatch
 * @param {object} globalState
 * @param {object} args
 * @param {string} args.hash getBriefsHash()
 * @param {object} args.filters
 * @param {string} args.timeZone
 * @returns {Promise|null}
 */
export const loadBoardExternalJobs = (
  dispatch,
  globalState,
  { hash, filters, timeZone }
) => {
  const board =
    globalState.projects2.briefs && globalState.projects2.briefs[hash];
  if (!board || !board.loaded) {
    return null;
  }
  const entities = (globalState.entities && globalState.entities.briefs) || {};
  const briefs = (board.briefs || []).map((id) => entities[id]).filter(Boolean);
  const key = getExternalJobsKey(hash, timeZone);
  const entry = globalState.externalJobs && globalState.externalJobs[key];
  if (entry && entry.loading) {
    return null;
  }

  const range = getExternalJobsRequest({
    briefs,
    moreToLoad: Boolean(board.moreToLoad),
    filters,
    entry,
    timeZone,
  });
  if (!range) {
    return null;
  }

  return dispatch(
    loadExternalJobDays(
      key,
      buildExternalJobsFilterParams(filters),
      range,
      timeZone
    )
  );
};
